import React, { useCallback, useMemo } from 'react'
import { Form, Input, Button, Typography, App } from 'antd'
import { UserOutlined, LockOutlined, EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import AuthService from '@/libs/services/auth'
const { Title } = Typography

const StyledForm = styled(Form)`
  width: 100%;
`

const SubmitButton = styled(Button)`
  height: 45px;
  background-color: #13c2c2;
  border-color: #13c2c2;
  &:hover {
    background-color: #36cfc9;
    border-color: #36cfc9;
  }
`

const FooterText = styled.div`
  text-align: center;
  margin-top: 24px;
`

const RegisterView = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const onFinish = useCallback(
    async values => {
      try {
        const response = await AuthService.register(values)

        if (response.status === 204) {
          notification.success({
            message: 'Success',
            description: 'Check your email for verification!',
            placement: 'topRight',
            duration: 3,
          })
          navigate('/auth/login', { replace: true })
        }
      } catch {
        notification.error({
          message: 'Error',
          description: 'Failed to create account. Please try again.',
          placement: 'topRight',
          duration: 3,
        })
        form.setFields([
          {
            name: 'password',
            errors: ['Failed to create account. Please try again.'],
          },
        ])
      }
    },
    [navigate, form, notification]
  )

  const formRules = useMemo(
    () => ({
      email: [
        { required: true, message: 'Please input your email!' },
        { type: 'email', message: 'Please enter a valid email' },
      ],
      password: [{ required: true, message: 'Please input your password!' }],
      first_name: [{ required: true, message: 'Please input your first name!' }],
      last_name: [{ required: true, message: 'Please input your last name!' }],
    }),
    []
  )

  return (
    <>
      <Title level={3}>Create Account</Title>

      <StyledForm form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <Form.Item name="email" rules={formRules.email} validateTrigger="onBlur">
          <Input prefix={<UserOutlined />} placeholder="Email" size="large" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="first_name"
              rules={formRules.first_name}
              validateTrigger="onBlur"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input prefix={<UserOutlined />} placeholder="Firstname" size="large" />
            </Form.Item>
            <Form.Item
              name="last_name"
              rules={formRules.last_name}
              validateTrigger="onBlur"
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input prefix={<UserOutlined />} placeholder="Lastname" size="large" />
            </Form.Item>
          </div>
        </Form.Item>
        <Form.Item name="password" rules={formRules.password} validateTrigger="onBlur">
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Password"
            size="large"
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item>
          <SubmitButton type="primary" htmlType="submit" block>
            Register
          </SubmitButton>
        </Form.Item>
      </StyledForm>

      <FooterText>
        Already have an account? <Link to="/auth/login">Login</Link>
      </FooterText>
    </>
  )
}

export default RegisterView
